import { LayersIcon } from "lucide-react";
import { use, useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  T,
  RecordProps,
  TLShapeUtilCanBindOpts,
  Editor,
  Rectangle2d,
  useEditor,
  useValue,
  createShapePropsMigrationSequence,
  createShapePropsMigrationIds,
} from "tldraw";
import { useSharedResizeObserver } from "use-shared-resize-observer";

import {
  BlockContainer,
  BlockContainerShapeContext,
  BlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  buildLayerShapes,
  compileLayers,
  deleteLayerShapes,
  getLayerShapes,
} from "@/components/canvas-editor/layers";
import {
  BindableUtil,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  CustomNodeRootProps,
  FeedProps,
  FeedShape,
  feedType,
  layerType,
  metadataType,
  pinnableShapes,
} from "@/components/canvas-editor/shapes/shapes";
import { FeatureFlagContext } from "@/lib/feature-flags";
import {
  LensSetting,
  isPersonalizedOrder,
  sortOrderLabels,
  FeedLens,
} from "@/lib/lens";
import { FeedVersion } from "@/lib/types";
import { cn } from "@/lib/utils";

const MIN_W = 600;
const MIN_H = 156;
const BASE_TOP_PADDING = 44;
/*
 * The Page box's geometry, measured against the Logic node it sits beside so
 * the two read as one system: children 16px from either side, 14px apart,
 * 16px above the bottom border.
 *
 *   Feed header 32 + 16 inset   → the box's top edge          PAGE_BOX_TOP
 *   + border 2 + its pt-1 (4)  → where its content starts    PAGE_CONTENT_TOP
 *   content … + PAGE_CONTENT_GAP → the first layer (= the gap between layers)
 *   last layer + PAGE_STACK_INSET → the box's bottom edge (= Logic's bottom pad)
 */
const PAGE_BOX_TOP = 48;
const PAGE_CONTENT_TOP = 54;
const PAGE_CONTENT_GAP = 14;
const PAGE_STACK_INSET = 16;

function createFeedMigrationSequence() {
  const versions = createShapePropsMigrationIds(feedType, {
    RemoveMinH: 1,
    insensitive: 2,
    loginInNode: 3,
    starSyntax: 4,
    defaultHeight: 5,
    removeCustomNode: 6,
  });
  return createShapePropsMigrationSequence({
    sequence: [
      { id: versions.RemoveMinH, up() {} },
      { id: versions.insensitive, up() {} },
      { id: versions.loginInNode, up() {} },
      { id: versions.starSyntax, up() {} },
      { id: versions.defaultHeight, up() {} },
      {
        id: versions.removeCustomNode,
        up(props) {
          delete (props as CustomNodeRootProps).showCustomNodeParameters;
          delete (props as CustomNodeRootProps).customNodeParameters;
        },
      },
    ],
  });
}

export class FeedShapeUtil extends BindableUtil<FeedShape> {
  static override migrations = createFeedMigrationSequence();
  static override type = feedType;

  override getType() {
    return feedType;
  }

  static override props: RecordProps<FeedShape> = {
    h: T.number,
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    order: T.literalEnum(
      "new",
      "blend",
      "trending",
      "old",
      "hackernews",
      "wilson",
      "personalized",
    ),
    metadata: metadataType,
    // 🔴 `custom_sort_settings` is FREE-FORM JSONB the serve path reads directly. The engines
    // ignore keys they do not know, so a key this build has never heard of is a valid feed
    // setting, not corrupt data — `max_days` sat in 95 live feeds for months before this file
    // learned of it. A `T.object` with only declared keys rejects such a key with "Unexpected
    // property", and a shape prop that fails validation does not degrade: tldraw refuses the
    // whole record and the canvas white-screens (2026-09-14, feed 9702 and 93 others, the day
    // `reconcileLayerShapes` began writing the stored settings onto this shape). So the object
    // tolerates undeclared keys, and every key the ui itself reads is still declared below.
    custom_sort_settings: T.object({
      decay_penalty: T.number.optional(),
      like_count_multiplier: T.number.optional(),
      reply_count_multiplier: T.number.optional(),
      repost_count_multiplier: T.number.optional(),
      reader_like_count_multiplier: T.number.optional(),
      reader_reply_count_multiplier: T.number.optional(),
      reader_repost_count_multiplier: T.number.optional(),
      request_less_count_multiplier: T.number.optional(),
      request_more_count_multiplier: T.number.optional(),
      time_window: T.number.optional(),
      thresholds: T.any.optional(),
      gravity: T.number.optional(),
      time_offset: T.number.optional(),
      // Wilson algorithm parameters
      wilson_confidence_z: T.number.optional(),
      wilson_interaction_like_multiplier: T.number.optional(),
      wilson_interaction_reply_multiplier: T.number.optional(),
      wilson_interaction_repost_multiplier: T.number.optional(),
      wilson_interaction_quote_multiplier: T.number.optional(),
      wilson_request_more_multiplier: T.number.optional(),
      wilson_request_less_multiplier: T.number.optional(),
      // For You algorithm parameters
      foryou_similarity_threshold: T.number.optional(),
      foryou_similar_users_limit: T.number.optional(),
      foryou_recent_posts_limit: T.number.optional(),
      foryou_half_life_hours: T.number.optional(),
      foryou_smoothing_factor: T.number.optional(),
      foryou_popularity_penalty: T.number.optional(),
      foryou_divisor_power: T.number.optional(),
      foryou_time_shift_hours: T.number.optional(),
      foryou_ignore_order: T.boolean.optional(),
      // Viewer-graph lens. A feed setting, not a filter node: it is applied at
      // serve time against the reader rather than at match time against the
      // post. See the `lens` comment on SortingParams in lib/types.ts.
      // Deliberately permissive: a preset name, or any object.
      //
      // It is tempting to validate the blend's shape here, and it would be
      // wrong. A shape prop that fails validation does not degrade — tldraw
      // refuses the whole record and the canvas white-screens, which is exactly
      // how the gallery-embed outage worked. `lens` lives in free-form JSONB, so
      // this build may well be handed a preset renamed since, a blend from a
      // later contract version, or something that was never a lens. Every one of
      // those means "no lens" to the reader, so it must render as off here
      // rather than take the editor down. `readableLens` is the gate.
      //
      // The cast is the honest expression of that split: at runtime this accepts
      // anything string-or-object so the canvas cannot be killed by stored data,
      // while the TS type stays `LensSetting` because that is what every read
      // goes through `readableLens` to obtain.
      lens: (
        T.or(T.string, T.unknownObject) as unknown as T.Validator<LensSetting>
      ).optional(),
      // Several audiences, each with a share. DELIBERATELY PERMISSIVE, for exactly the reason the
      // comment above gives: this key lives in free-form JSONB, so this build may be handed a list
      // from a later contract, a list of two when the minimum becomes three, or something that was
      // never a lens list. Every one of those means "not a list" to the reader, so it has to render
      // as off rather than take the editor down. `readableLensList` is the gate.
      //
      // Declared as well as tolerated, so the ui's own reads have a typed shape to lean on.
      lenses: (T.arrayOf(T.unknownObject) as unknown as T.Validator<FeedLens[]>).optional(),
      // How far back the trending/scoring queries look, in days. Read by feed-processor's
      // `algo_new_async` (`params.get('max_days', 60)`); never written by the ui, only carried.
      max_days: T.number.optional(),
    })
      .allowUnknownProperties()
      .nullable()
      .optional(),
    padding: T.object({
      top: T.number,
      right: T.number,
      bottom: T.number,
      left: T.number,
      gap: T.number,
    }),
    title: T.string.optional(),
    description: T.string.optional(),
    hideSortSettings: T.boolean.optional(),
  };

  override getDefaultProps() {
    return {
      w: MIN_W,
      minW: MIN_W,
      h: MIN_H,
      order: "new" as const,
      padding: {
        top: BASE_TOP_PADDING + 40,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      title: "Feed",
      hideSortSettings: false,
      metadata: undefined,
    };
  }

  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<FeedShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return (
      fromShapeType === feedType &&
      (pinnableShapes.includes(toShapeType) || toShapeType === layerType) &&
      bindingType === "layout"
    );
  }

  override canResize() {
    return true;
  }

  override component(shape: FeedShape) {
    return <Feed shape={shape} updateShape={this.updateProps.bind(this)} />;
  }

  override getGeometry(shape: FeedShape) {
    return new Rectangle2d({
      width: Math.max(shape.props.minW || 0, shape.props.w),
      height: Math.max(MIN_H, shape.props.h),
      isFilled: true,
    });
  }

  override indicator(shape: FeedShape) {
    return (
      <rect
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={Math.max(MIN_H, shape.props.h)}
      />
    );
  }
}

/**
 * The Feed's PAGE section header: what the page is divided into, in one line,
 * and the way to add a layer. The layers themselves are blocks pinned below
 * this header; the rules root sits below them.
 *
 * 🔴 THE LAYER BLOCKS ARE THE SORT CONTROL, for every feed. A single-sort feed
 * renders as one layer holding the whole page — which is what it is — so the
 * layered model is visible on a creator's own feed and "Add a layer" is the
 * obvious next step rather than a hidden mode. Nothing is stored until they
 * change something: that is the lazy migration (see `layers.ts`).
 *
 * The plain select survives only for the orderings that cannot be a layer at
 * all (`personalized`, `foryou` — served by a different system entirely).
 */
function PageSection({
  shape,
  updateShape,
}: {
  shape: FeedShape;
  updateShape: (shape: FeedShape, props: Partial<FeedProps>) => void;
}) {
  const featureFlags = use(FeatureFlagContext);
  const hasNewOrders = featureFlags.includes("new_orders");
  const editor = useEditor();
  const layers = useValue("feed-layers", () => compileLayers(editor), [editor]);
  const personalized = isPersonalizedOrder(shape.props.order);

  return (
    <div className="page-summary flex flex-col gap-2 px-2.5">
      {personalized || layers.length === 0 ? (
        <div className="flex items-center gap-2">
          <CanvasSimpleSelect
            aria-label="Sorting"
            name="sticky_type"
            tooltip="Sorting"
            value={shape.props.order}
            options={[
              { value: "new", label: sortOrderLabels.new },
              { value: "trending", label: sortOrderLabels.trending },
              { value: "blend", label: sortOrderLabels.blend },
              { value: "old", label: sortOrderLabels.old },
              { value: "hackernews", label: sortOrderLabels.hackernews },
              { value: "personalized", label: "Personalized" },
              ...(hasNewOrders
                ? [{ value: "wilson", label: sortOrderLabels.wilson }]
                : []),
            ]}
            className="flex-1"
            placeholder="Sorting"
            onValueChange={(v) => {
              const order = v as FeedVersion["order"];
              // Leaving a Personalized ordering is the moment a feed gains its
              // layer blocks; they are drawn from the settings as they now are.
              editor.run(() => {
                updateShape(shape, { order });
                deleteLayerShapes(editor);
                if (order && !isPersonalizedOrder(order)) {
                  const current = editor.getShape<FeedShape>(shape.id);
                  if (current) buildLayerShapes(editor, current);
                }
              });
            }}
          />
          {personalized && (
            <span className="text-muted-foreground text-xs">
              Personalized feeds are served by a different system and cannot be layered.
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Feed({
  shape,
  updateShape,
}: {
  shape: FeedShape;
  updateShape: (shape: FeedShape, props: Partial<FeedProps>) => void;
}) {
  const editor = useEditor();
  const id = useValue(
    "logic-atom",
    () => {
      return dropTargetId.get();
    },
    [dropTargetId],
  );
  // Where the Page section ends, so the "Rules" divider can sit between the
  // two stacks the layout binding draws: after the last layer, before the root.
  const pageStack = useValue(
    "feed-page-stack",
    () => {
      const layers = getLayerShapes(editor);
      if (layers.length === 0) return 0;
      return (
        layers.reduce((sum, l) => sum + l.props.h, 0) +
        (layers.length - 1) * shape.props.padding.gap
      );
    },
    [editor, shape.props.padding.gap],
  );
  // The Page box's own content (header, summary, switch), measured so the
  // layer stack starts below it and the box can be sized to enclose the stack.
  const pageContentRef = useRef<HTMLDivElement>(null);
  const [pageContentH, setPageContentH] = useState(0);
  // Idempotent on purpose: the padding is SET from the measured height, never
  // nudged by a delta. A throttled, delta-based writer once kept a reading
  // from taller content after the content had shrunk, and the layers hung
  // 66px below the box. The layout binding recomputes the Feed's own height
  // from the padding, so only the padding is written here.
  const onPageContentResize = useCallback(
    (h: number) => {
      setPageContentH(h);
      const top = PAGE_CONTENT_TOP + h + PAGE_CONTENT_GAP;
      const current = editor.getShape<FeedShape>(shape.id);
      if (!current || current.props.padding.top === top) return;
      // Measurement, not authorship: it must not become an undo step, and
      // must not make Save think the author edited the canvas (getCanUndo).
      editor.run(
        () => updateShape(current, { padding: { ...current.props.padding, top } }),
        { history: "ignore" },
      );
    },
    [editor, shape.id, updateShape],
  );
  useSharedResizeObserver({
    ref: pageContentRef,
    onUpdate: (entry) => {
      const box = entry.borderBoxSize[0];
      if (!box?.blockSize) return;
      onPageContentResize(box.blockSize);
    },
  });
  // The observer reports CHANGES. A canvas whose Page content never resizes
  // after mount (gym availability already cached, nothing toggled) would
  // never report at all, and the box would frame only its own content with
  // the layers hanging below it. So read the height straight after every
  // commit as well; the callback is a no-op when nothing moved.
  useLayoutEffect(() => {
    const el = pageContentRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    if (h > 0 && h !== pageContentH) onPageContentResize(h);
  });

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={Math.max(MIN_H, shape.props.h)}
        className={cn(
          "flex flex-col gap-2",
          shape.id === id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        onUpdateDimensions={({ h }) => {
          if (!shape.props.hideSortSettings) {
            return;
          }
          updateShape(shape, {
            h,
            padding: {
              ...shape.props.padding,
              top: 44,
            },
          });
        }}
      >
        <BlockHeader
          description={
            shape.props.description || (
              <div className="flex flex-col gap-2">
                <p>
                  This block is your feed. It has two parts, and they save together
                  when you press Save.
                </p>
                <p>
                  <strong>Page</strong> (top, the layers icon) decides how each page
                  a reader sees is put together: which orderings, and how much of
                  the page each one gets.
                </p>
                <p>
                  <strong>Rules</strong> (below, the filter icon) decide which posts
                  are allowed into the feed at all. A post has to pass the rules
                  before any layer can show it.
                </p>
              </div>
            )
          }
        >
          {shape.props.title || "Feed"}
        </BlockHeader>

        {shape.props.hideSortSettings ? null : (
          // The Page container ENCLOSES its layers. The layers are separate
          // shapes the layout binding stacks below this box's content, so the
          // box is given an explicit height — its own content plus the layer
          // stack — rather than measured, or the stack would push the content
          // height, which sets where the stack starts, which grows the box…
          <BlockContainer
            asDiv={true}
            // A warm neutral, so every layer's own colour reads against it;
            // amber was also the Trending layer's colour and the two merged.
            variant="bronze"
            // The same 16px inset from the Feed's edges as the rules root
            // below it, so the two sections share one frame line.
            className="mx-3.5 mt-3.5 px-1 pt-1 flex flex-col gap-1 group pointer-events-auto sort-area-block"
            height={
              pageContentH > 0
                ? PAGE_CONTENT_TOP -
                  PAGE_BOX_TOP +
                  pageContentH +
                  (pageStack > 0 ? PAGE_CONTENT_GAP + pageStack + PAGE_STACK_INSET : 0)
                : undefined
            }
          >
            <div ref={pageContentRef} className="flex flex-col gap-1">
              <BlockHeader
                description={
                  <div className="flex flex-col gap-2">
                    <p>
                      Every page a reader sees is built from the layers in this box.
                      Each layer picks an ordering (newest first, trending, and so on)
                      and takes a share of the page; posts from all the layers are
                      mixed in proportion, and when two layers pick the same post the
                      one higher up gets it.
                    </p>
                    <p>
                      Add layers from the Page palette (the layers icon at the left),
                      drag them to reorder, and use a layer&rsquo;s Only show / Move
                      up buttons to give it an audience based on each reader&rsquo;s
                      own follows.
                    </p>
                  </div>
                }
              >
                <span className="flex items-center gap-1.5">
                  <LayersIcon className="h-3.5 w-3.5" />
                  Page
                </span>
              </BlockHeader>
              <PageSection shape={shape} updateShape={updateShape} />
            </div>
          </BlockContainer>
        )}

      </BlockContainer>
    </BlockContainerShapeContext>
  );
}

export function getFeedShape(editor: Editor) {
  return editor.getCurrentPageShapes().find((s) => s.type === feedType) as
    | FeedShape
    | undefined;
}
